<template>
  <v-container fluid class="pa-6">
    <v-row align="center" class="mb-3">
      <div class="ml-2">
        <h2 class="text-h5 font-weight-bold">
          {{ isNew ? $t('message.new_automation') : sequence.name }}
        </h2>
      </div>
      <v-spacer></v-spacer>
      <v-switch v-model="sequence.isActive" :label="sequence.isActive ? 'Active' : 'Inactive'" color="success"
        hide-details class="mr-6"></v-switch>
      <v-btn outlined color="primary" large :loading="saving" :disabled="!isValid" @click="saveSequence">
        {{ $t('message.save') }}
      </v-btn>
    </v-row>
    <v-card-text class="mt-0 pt-0">
      {{ $t('message.lead_automations_explained') }}
    </v-card-text>

    <v-row v-if="!loading">
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4 mb-4">
          <div class="text-subtitle-1 mb-4 font-weight-bold">{{ $t('message.configuration') }}</div>
          <v-text-field v-model="sequence.name" label="Automation Name" outlined dense class="mb-2"></v-text-field>
          <v-select v-model="sequence.triggerEvent" :items="triggerOptions" label="Trigger Event" :disabled="!isNew"
            outlined dense persistent-hint :hint="!isNew ? 'Trigger cannot be changed after creation' : ''"></v-select>
          <v-alert v-if="triggerEventHint" type="info" text dense class="mt-3 text-caption">
            {{ $t(triggerEventHint) }}
          </v-alert>
        </v-card>

        <v-card outlined class="pa-4 mb-4">
          <div class="text-subtitle-1 mb-2 font-weight-bold">
            <v-icon small left color="error">mdi-octagon-outline</v-icon>
            {{ $t('message.stop_conditions') }}
          </div>
          <div class="text-caption mb-3">{{ $t('message.stop_sequence_if') }}:</div>
          <v-checkbox :disabled="true" v-model="sequence.stopConditions.exitOnStatusChange"
            :label="$t('message.they_become_member')" value="member" dense hide-details class="mt-1"></v-checkbox>
          <v-checkbox v-model="sequence.stopConditions.exitOnStatusChange" :label="$t('message.they_marked_inactive')"
            value="inactive" dense hide-details class="mt-1"></v-checkbox>
          <v-checkbox v-model="sequence.stopConditions.exitOnEventMatch" :label="$t('message.they_check_in')"
            value="first_checkin" dense hide-details class="mt-1"></v-checkbox>
          <v-checkbox v-if="isValidStopCondition('buy_membership')" v-model="sequence.stopConditions.exitOnEventMatch"
            :label="$t('message.they_buy_membership')" value="membership_purchased" dense hide-details
            class="mt-1"></v-checkbox>
          <div class="text-caption mb-3">*{{
            $t('message.select_at_least_one') }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <div class="text-h6 mb-4">{{ $t('message.steps') }}</div>
        <v-timeline dense align-top v-if="sequence.steps && sequence.steps.length > 0">
          <v-timeline-item v-for="(step, index) in sequence.steps" :key="index" color="primary" small>
            <v-card outlined>
              <v-toolbar flat dense>
                <span class="text-caption font-weight-bold">STEP {{ index + 1 }}</span>
                <v-spacer></v-spacer>
                <v-btn icon x-small color="error" @click="removeStep(index)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>

              <v-card-text>
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field v-model.number="step.delayDays" type="number" label="Wait Days" outlined dense
                      :min="index === 0 ? 1 : sequence.steps[index - 1].delayDays + 1" @input="validateDelay(index)"
                      :hint="index > 0 ? `Must be > Step ${index} (${sequence.steps[index - 1].delayDays})` : ''"
                      persistent-hint></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field v-model="step.preferredTime" type="time" label="Send At" outlined
                      dense></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-select v-model="step.channel" :items="channelOptions" label="Channel" outlined dense></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete v-model="step.templateId" :items="templates" item-text="name" item-value="_id"
                      label="Email Template" outlined dense placeholder="Select a template..."></v-autocomplete>
                    <v-btn v-if="step.templateId" small text color="primary" class="mt-1" @click="openTemplatePreview(step.templateId)">
                      <v-icon small left>mdi-eye-outline</v-icon>View Template
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>

        <v-btn block outlined color="primary" class="mt-4" @click="addStep">
          <v-icon left>mdi-plus</v-icon> {{ $t('message.add_step') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-skeleton-loader v-else type="article, actions"></v-skeleton-loader>

    <template v-if="!loading && !isNew">
      <v-row v-if="aggregates" class="mt-4">
        <v-col cols="12" sm="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-overline">{{ $t('message.emails_today') }}</div>
            <div class="text-h4">{{ aggregates.today }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-overline">{{ $t('message.emails_this_month') }}</div>
            <div class="text-h4">{{ aggregates.thisMonth }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-overline">{{ $t('message.emails_last_month') }}</div>
            <div class="text-h4">{{ aggregates.lastMonth }}</div>
          </v-card>
        </v-col>
        <v-col cols="12" sm="3">
          <v-card outlined class="pa-4 text-center">
            <div class="text-overline">{{ $t('message.emails_this_year') }}</div>
            <div class="text-h4">{{ aggregates.thisYear }}</div>
          </v-card>
        </v-col>
      </v-row>

      <v-expansion-panels class="mt-2">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div @click="loadSequenceEmails(false)">
              <v-icon>mdi-email-multiple-outline</v-icon>
              {{ $t('message.sequence_emails') }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-skeleton-loader v-if="loadingEmails && !emails.length"
              type="list-item-avatar, divider, list-item-avatar, divider, list-item-avatar"></v-skeleton-loader>
            <div v-else-if="!emails.length" class="text-center text-subtitle-1 primary--text">
              {{ $t('message.no_emails') }}
            </div>
            <template v-else>
              <template v-for="(e, index) in emails">
                <v-list-item two-line :key="'seq_email_' + e._id" class="px-0 text-left" @click="getEmail(e)">
                  <v-list-item-content>
                    <v-list-item-title>
                      <span v-if="e.opening">
                        <v-progress-circular size="15" width="5" indeterminate color="secondary"></v-progress-circular>
                      </span>
                      <span v-else>{{ e.subject }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ $d(new Date(e.time), 'short', $i18n.locale) }}
                      <v-icon class="ml-1" size="20" v-if="e.failed" color="error">mdi-email-alert-outline</v-icon>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-badge color="secondary" :content="e.recipients">
                      <v-icon size="20" v-if="e.recipients == 1">mdi-account-outline</v-icon>
                      <v-icon size="20" v-else-if="e.recipients == 2">mdi-account-multiple-outline</v-icon>
                      <v-icon size="20" v-else>mdi-account-group-outline</v-icon>
                    </v-badge>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index + 1 < emails.length" :key="'seq_email_div_' + index"></v-divider>
              </template>
              <v-card-actions v-if="emailsHasMore">
                <v-spacer></v-spacer>
                <v-btn outlined small color="primary" :loading="loadingEmails" @click="loadSequenceEmails(true)">
                  {{ $t('message.view_more') }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>

    <v-dialog v-model="emailDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card outlined>
        <v-toolbar dense flat class="opposite--text" color="primary">
          <v-spacer></v-spacer>
          <v-btn icon small @click="emailDialog = false; email = null;">
            <v-icon color="opposite">mdi-close-circle-outline</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card v-if="email" class="pa-2">
          <v-card-actions style="color: gray">
            <v-avatar size="20">
              <ImageDisplay v-if="email.sender && email.sender.userImageSmall" :src="email.sender.userImageSmall" />
              <img v-else :src="assets('default-profile-avatar.png')" />
            </v-avatar>
            <span v-if="email.sender" class="ml-1">
              {{ email.sender.displayName }}
            </span>
            <v-spacer></v-spacer>
            {{ $d(new Date(email.time), 'short', $i18n.locale) }}
          </v-card-actions>
          <v-card-title v-if="email.error" class="text-caption error--text">
            {{ email.error }}
          </v-card-title>
          <v-card class="pa-2" outlined>
            <v-card-title>
              <span class="text-subtitle-1">{{ $t('message.recipients') }}:</span>
              <template v-for="(r, index) in email.recipients">
                <v-chip :key="'recipient' + index" v-if="r && r != ''" small class="ma-1">{{ r }}</v-chip>
              </template>
            </v-card-title>
            <v-card-title>
              <span class="text-subtitle-1">{{ $t('message.subject') }}:</span>
              <span style="margin-left: 5px">{{ email.subject }}</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text v-html="email.contents"></v-card-text>
          </v-card>
          <v-card outlined class="mt-1" v-if="email.openedBy && email.openedBy.length">
            <v-card-title>
              <span class="text-subtitle-1">{{ $t('message.opened_by') }}:</span>
            </v-card-title>
            <v-list>
              <v-list-item dense v-for="i in email.openedBy" :key="'opened_email_' + i._id">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ i.user ? i.user.email : i.email }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $d(new Date(i.time), 'long', $i18n.locale) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-card>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" max-width="800">
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">{{ previewTemplateName }}</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
          <div v-html="previewHtml"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small elevation="0" @click="previewDialog = false">{{ $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Api from "@/services/Api";

export default {
  name: "AutomatedSequenceBuilder",
  data() {
    return {
      managedAcademyId: this.$store.state.managedAcademy,
      sequenceId: this.$route.params.id,
      isNew: this.$route.params.id === "new",
      loading: true,
      saving: false,
      templates: [],
      channelOptions: [
        { text: 'Email', value: 'email' },
        //{ text: 'WhatsApp', value: 'whatsapp' }
      ],
      triggerOptions: [
        { text: this.$t('message.new_inquiry'), value: "inquiry_created" },
        { text: this.$t('message.new_visitor'), value: "visitor_created" },
        { text: this.$t('message.trial_class_booked'), value: "trial_booked" },
        { text: this.$t('message.first_checkin'), value: "first_checkin" },
        { text: this.$t('message.membership_purchased'), value: "membership_purchased" },
        { text: this.$t('message.on_demand'), value: "on_demand" },
        //{ text: this.$t('message.birthday'), value: "birthday_milestone" }
      ],
      previewDialog: false,
      previewHtml: null,
      previewTemplateName: null,
      aggregates: null,
      emails: [],
      emailsIndex: 0,
      emailsHasMore: false,
      loadingEmails: false,
      email: null,
      emailDialog: false,
      sequence: {
        name: "",
        managedAcademy: null,
        triggerEvent: "visitor_created",
        isActive: true,
        steps: [],
        stopConditions: {
          exitOnStatusChange: ['member'],
          exitOnEventMatch: []
        }
      }
    };
  },
  computed: {
    triggerEventHint() {
      const hints = new Map([
        ['inquiry_created', 'message.trigger_hint_inquiry_created'],
        ['visitor_created', 'message.trigger_hint_visitor_created'],
        ['trial_booked', 'message.trigger_hint_trial_booked'],
        ['first_checkin', 'message.trigger_hint_first_checkin'],
        ['membership_purchased', 'message.trigger_hint_membership_purchased'],
        ['on_demand', 'message.trigger_hint_on_demand'],
      ]);
      return hints.get(this.sequence.triggerEvent) || null;
    },
    isValid() {
      const hasName = !!this.sequence.name;
      const hasSteps = this.sequence.steps && this.sequence.steps.length > 0;
      const stepsComplete = hasSteps && this.sequence.steps.every(s => s.templateId && s.delayDays >= 1);

      // ADD THIS: Ensure at least one checkbox is checked in either Status or Event
      const hasStopCondition =
        (this.sequence.stopConditions.exitOnStatusChange && this.sequence.stopConditions.exitOnStatusChange.length > 0) ||
        (this.sequence.stopConditions.exitOnEventMatch && this.sequence.stopConditions.exitOnEventMatch.length > 0);

      return hasName && stepsComplete && hasStopCondition;
    }
  },
  async created() {
    this.managedAcademyId = this.$store.state.managedAcademy;
    await this.fetchTemplates();
    if (!this.isNew) {
      await this.loadSequence();
      this.fetchSequenceAggregates();
    } else {
      this.sequence.managedAcademy = this.managedAcademyId;
      this.addStep();
      this.loading = false;
    }
  },
  methods: {
    isValidStopCondition(stopCondition) {
      if (stopCondition == 'buy_membership') {
        if (this.sequence.triggerEvent.indexOf('membership_purchased') >= 0) {
          return false;
        }
      }
      return true;
    },
    validateDelay(index) {
      const step = this.sequence.steps[index];

      // Rule for Step 1
      if (index === 0) {
        if (step.delayDays < 1) step.delayDays = 1;
      }
      // Rule for subsequent steps
      else {
        const previousDelay = this.sequence.steps[index - 1].delayDays;
        if (step.delayDays <= previousDelay) {
          step.delayDays = previousDelay + 1;
        }
      }
    },
    async fetchTemplates() {
      try {
        const response = await Api().get(`documents/academy/${this.managedAcademyId}/email/templates?archived=false`);
        if (response.data && response.data.templates) {
          this.templates = response.data.templates;
        }
      } catch (err) {
        console.error("Template fetch failed", err);
      }
    },
    async loadSequence() {
      this.loading = true;
      try {
        const res = await Api().get(`automations/managedacademy/${this.managedAcademyId}/sequence/${this.sequenceId}`);
        if (res.data) {
          const data = res.data;
          if (!data.steps) data.steps = [];
          if (!data.stopConditions) data.stopConditions = { exitOnStatusChange: [], exitOnEventMatch: [] };
          this.sequence = data;
        }
      } catch (err) {
        console.error("Load error", err);
      } finally {
        this.loading = false;
      }
    },
    addStep() {
      const lastStep = this.sequence.steps[this.sequence.steps.length - 1];
      const nextDelay = lastStep ? lastStep.delayDays + 1 : 1;

      this.sequence.steps.push({
        stepOrder: this.sequence.steps.length + 1,
        delayDays: nextDelay,
        preferredTime: "07:00",
        channel: "email",
        templateId: null,
        conditions: { requireCheckin: false, requireNoCheckin: false }
      });
    },
    removeStep(index) {
      this.sequence.steps.splice(index, 1);
      this.sequence.steps.forEach((s, i) => s.stepOrder = i + 1);
    },
    async saveSequence() {
      this.saving = true;
      try {
        this.sequence.managedAcademy = this.managedAcademyId;
        await Api().post(`automations/managedacademy/${this.managedAcademyId}`, this.sequence);
        this.goBack();
      } catch (err) {
        console.error("Save error", err);
      } finally {
        this.saving = false;
      }
    },
    openTemplatePreview(templateId) {
      const template = this.templates.find(t => t._id === templateId);
      if (template) {
        this.previewHtml = template.contents;
        this.previewTemplateName = template.name;
        this.previewDialog = true;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    async fetchSequenceAggregates() {
      try {
        const res = await Api().get(`automations/managedacademy/${this.managedAcademyId}/sequence/${this.sequenceId}/email/aggregates`);
        this.aggregates = res.data;
      } catch (err) {
        console.error("Sequence aggregates error", err);
      }
    },
    async loadSequenceEmails(append) {
      this.loadingEmails = true;
      if (!append) {
        this.emailsIndex = 0;
        this.emails = [];
      }
      try {
        const res = await Api().get(`automations/managedacademy/${this.managedAcademyId}/sequence/${this.sequenceId}/emails?index=${this.emailsIndex}`);
        if (res.data) {
          this.emails = this.emails.concat(res.data.emails || []);
          this.emailsIndex += res.data.count || 0;
          this.emailsHasMore = !!res.data.hasMore;
        }
      } catch (err) {
        console.error("Sequence emails error", err);
      } finally {
        this.loadingEmails = false;
      }
    },
    getEmail(e) {
      e.opening = true;
      Api().get(`admins/academy/${this.managedAcademyId}/emails/${e._id}`)
        .then((response) => {
          this.email = response.data.email;
          this.emailDialog = true;
          e.opening = false;
        })
        .catch(() => {
          e.opening = false;
        });
    }
  }
};
</script>